import { useEffect, useMemo, useRef, useState } from "react";
import { Edit, Layers, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { buildRowLabel } from "@/lib/table-utils";

type GroupMember = {
  tableId: string;
  rowIndex: number;
};

type Group = {
  _id: string;
  name: string;
  members: GroupMember[];
  directions?: string;
};

type TableLike = {
  _id: string;
  name: string;
  headers: string[];
  rows: string[][];
  selectedForSubmittal: boolean;
};

type SelectedRow = {
  tableId: string;
  tableName: string;
  rowIndex: number;
  label: string;
  headers: string[];
  rowData: string[];
};

interface GroupManagerProps {
  tables: TableLike[];
  selectedRows: Map<string, Set<number>>;
  rowGroups: Group[];
  createGroupRequest?: { id: number; tableId: string } | null;
  onCreateGroup: (args: {
    name: string;
    members: GroupMember[];
    directions?: string;
  }) => Promise<void>;
  onUpdateGroup: (
    groupId: string,
    args: { name?: string; members?: GroupMember[]; directions?: string }
  ) => Promise<void>;
  onDeleteGroup: (groupId: string) => Promise<void>;
  onClearSelection: () => void;
}

export function GroupManager({
  tables,
  selectedRows,
  rowGroups,
  createGroupRequest,
  onCreateGroup,
  onUpdateGroup,
  onDeleteGroup,
  onClearSelection,
}: GroupManagerProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [name, setName] = useState("");
  const [directions, setDirections] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeletingGroupId, setIsDeletingGroupId] = useState<string | null>(null);
  const lastCreateRequestId = useRef<number | null>(null);
  
  // Row detail modal state
  const [viewingMember, setViewingMember] = useState<SelectedRow | null>(null);

  const selectedMembers = useMemo<SelectedRow[]>(() => {
    const output: SelectedRow[] = [];
    for (const [tableId, indices] of selectedRows.entries()) {
      const table = tables.find((item) => item._id === tableId);
      if (!table || !table.selectedForSubmittal) continue;

      const sortedIndices = [...indices].sort((a, b) => a - b);
      for (const rowIndex of sortedIndices) {
        const row = table.rows[rowIndex] || [];
        output.push({
          tableId,
          tableName: table.name || "Untitled Table",
          rowIndex,
          label: buildRowLabel(row),
          headers: table.headers || [],
          rowData: row,
        });
      }
    }
    return output;
  }, [selectedRows, tables]);

  const memberLookup = useMemo(() => {
    const map = new Map<string, SelectedRow>();
    for (const table of tables) {
      for (let i = 0; i < table.rows.length; i += 1) {
        map.set(`${table._id}:${i}`, {
          tableId: table._id,
          tableName: table.name || "Untitled Table",
          rowIndex: i,
          label: buildRowLabel(table.rows[i] || []),
          headers: table.headers || [],
          rowData: table.rows[i] || [],
        });
      }
    }
    return map;
  }, [tables]);

  const selectedTableIds = new Set(selectedMembers.map((member) => member.tableId));
  const singleTableSelection =
    selectedTableIds.size === 1
      ? tables.find((table) => table._id === [...selectedTableIds][0]) ?? null
      : null;

  function openCreateDialog() {
    setDialogMode("create");
    setEditingGroup(null);
    setName(singleTableSelection?.name || "");
    setDirections("");
    setDialogOpen(true);
  }

  useEffect(() => {
    if (!createGroupRequest) return;
    if (lastCreateRequestId.current === createGroupRequest.id) return;

    const table = tables.find((item) => item._id === createGroupRequest.tableId);
    if (!table) return;

    lastCreateRequestId.current = createGroupRequest.id;
    setDialogMode("create");
    setEditingGroup(null);
    setName(table.name || "");
    setDirections("");
    setDialogOpen(true);
  }, [createGroupRequest, tables]);

  function openEditDialog(group: Group) {
    setDialogMode("edit");
    setEditingGroup(group);
    setName(group.name);
    setDirections(group.directions || "");
    setDialogOpen(true);
  }

  async function handleSubmit() {
    const trimmedName = name.trim();
    const trimmedDirections = directions.trim();

    if (dialogMode === "create") {
      if (selectedMembers.length === 0) {
        toast.error("Select at least one row to merge");
        return;
      }

      const finalName = trimmedName || singleTableSelection?.name?.trim() || "";
      const groupMembers = selectedMembers.map((member) => ({
        tableId: member.tableId,
        rowIndex: member.rowIndex,
      }));

      if (!finalName) {
        toast.error("Merge name is required");
        return;
      }

      setIsSaving(true);
      try {
        await onCreateGroup({
          name: finalName,
          members: groupMembers,
          directions: trimmedDirections || undefined,
        });
        setDialogOpen(false);
        onClearSelection();
      } catch (error) {
        console.error("Failed to create merged rows:", error);
        toast.error(
          error instanceof Error ? error.message : "Failed to save merge"
        );
      } finally {
        setIsSaving(false);
      }
      return;
    }

    if (!editingGroup) return;
    if (!trimmedName) {
      toast.error("Merge name is required");
      return;
    }

    setIsSaving(true);
    try {
      await onUpdateGroup(editingGroup._id, {
        name: trimmedName,
        directions: trimmedDirections,
      });
      setDialogOpen(false);
    } catch (error) {
      console.error("Failed to update merged rows:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to save merge"
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(groupId: string) {
    setIsDeletingGroupId(groupId);
    try {
      await onDeleteGroup(groupId);
    } catch (error) {
      toast.error("Failed to delete merged rows");
      console.error("Error deleting merged rows:", error);
    } finally {
      setIsDeletingGroupId(null);
    }
  }

  const editingMembers =
    dialogMode === "create"
      ? selectedMembers
      : (editingGroup?.members
          .map((member) => memberLookup.get(`${member.tableId}:${member.rowIndex}`))
          .filter((member): member is SelectedRow => Boolean(member)) ?? []);

  return (
    <div className="space-y-4">
      <Card className="py-4">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">Merge Rows</CardTitle>
            {selectedMembers.length > 0 && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Layers className="size-4" />
                  <span>{selectedMembers.length} rows selected to merge</span>
                </div>
                <Button size="sm" variant="outline" onClick={onClearSelection}>
                  Clear
                </Button>
                <Button size="sm" onClick={openCreateDialog}>
                  Merge Rows
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {rowGroups.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No merged rows yet. Select rows below or use a table's Merge Rows action.
            </p>
          ) : (
            rowGroups.map((group) => {
              const memberTableIds = [...new Set(group.members.map((member) => member.tableId))];
              const tableNames = [
                ...new Set(
                  group.members
                    .map((member) => tables.find((table) => table._id === member.tableId)?.name)
                    .filter((name): name is string => Boolean(name))
                ),
              ];
              const unselectedTableCount = memberTableIds.filter((tableId) => {
                const table = tables.find((item) => item._id === tableId);
                return !table || !table.selectedForSubmittal;
              }).length;
              const isActive = unselectedTableCount === 0;

              return (
                <div
                  key={group._id}
                  className="rounded-md border p-3 text-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="space-y-1">
                      <p className="font-medium">{group.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {group.members.length} row{group.members.length !== 1 ? "s" : ""} from{" "}
                        {tableNames.length} table{tableNames.length !== 1 ? "s" : ""}
                      </p>
                      {!isActive && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          Inactive: reselect {unselectedTableCount} table
                          {unselectedTableCount !== 1 ? "s" : ""} to apply this group.
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1">
                        {tableNames.map((tableName) => (
                          <Badge key={`${group._id}-${tableName}`} variant="outline">
                            {tableName}
                          </Badge>
                        ))}
                        <Badge variant={isActive ? "secondary" : "outline"}>
                          {isActive ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      {group.directions && (
                        <p className="text-xs text-muted-foreground">{group.directions}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(group)}
                      >
                        <Edit className="size-3.5" />
                        Edit
                      </Button>
                      <ConfirmationDialog
                        title="Delete merged rows?"
                        description={`Delete "${group.name}" and remove its merge rules? This action cannot be undone.`}
                        onConfirm={() => handleDelete(group._id)}
                        disabled={isDeletingGroupId === group._id}
                        confirmLabel={isDeletingGroupId === group._id ? "Deleting..." : "Delete"}
                        confirmVariant="destructive"
                      >
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={isDeletingGroupId === group._id}
                        >
                          <Trash2 className="size-3.5" />
                          {isDeletingGroupId === group._id ? "Deleting..." : "Delete"}
                        </Button>
                      </ConfirmationDialog>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Merge Rows Edit/Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[90vw] max-w-5xl p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b-2 border-border">
            <DialogTitle>
              {dialogMode === "create" ? "Merge Rows" : "Edit Merged Rows"}
            </DialogTitle>
            <DialogDescription>
              Merged rows become one submittal line item during generation.
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="group-name">Merge Name</Label>
              <Input
                id="group-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Appliance Schedule"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="group-directions">Directions (optional)</Label>
              <textarea
                id="group-directions"
                value={directions}
                onChange={(event) => setDirections(event.target.value)}
                placeholder="Focus on compliance notes and warranty requirements."
                className="min-h-24 w-full rounded-md border bg-background px-3 py-2 text-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="space-y-2">
              <Label>Members</Label>
              <div className="max-h-[50vh] space-y-1 overflow-y-auto rounded-md border p-3 text-sm">
                {editingMembers.map((member) => (
                  <div 
                    key={`${member.tableId}:${member.rowIndex}`}
                    className="flex items-center justify-between p-2 rounded-md bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => setViewingMember(member)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{member.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {member.tableName} • Row {member.rowIndex + 1}
                      </p>
                    </div>
                  </div>
                ))}
                {editingMembers.length === 0 && (
                  <p className="text-muted-foreground text-center py-4">No members selected</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t-2 border-border">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSaving}>
              {isSaving
                ? "Saving..."
                : dialogMode === "create"
                  ? "Merge Rows"
                  : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Row Detail Modal - Updated to match requirements */}
      <Dialog 
        open={viewingMember !== null} 
        onOpenChange={(open) => !open && setViewingMember(null)}
      >
        <DialogContent className="w-[90vw] max-w-5xl p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b-2 border-border">
            <DialogTitle>Row Details</DialogTitle>
            <DialogDescription>
              {viewingMember && (
                <>
                  {viewingMember.tableName} • Row {viewingMember.rowIndex + 1}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {viewingMember && (
            <div className="p-6 space-y-4">
              {viewingMember.headers.map((header, colIndex) => {
                const value = viewingMember.rowData[colIndex];
                if (!value?.trim()) return null;
                
                return (
                  <div key={colIndex} className="border-b border-border pb-3 last:border-0">
                    <span className="font-bold text-muted-foreground text-xs uppercase tracking-wider block mb-1.5">
                      {header}
                    </span>
                    <span className="text-foreground font-medium text-base">
                      {value}
                    </span>
                  </div>
                );
              })}
              {viewingMember.rowData.every((cell) => !cell?.trim()) && (
                <p className="text-center text-muted-foreground py-8">No data in this row</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
